import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  BdGrantsService,
  ShareableGrant,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  ShareableObj,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { ButtonDirective } from 'primeng/button';
import { bdDialogCloseBehavior, BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  Step,
  StepList,
  StepPanel,
  StepPanels,
  Stepper,
} from 'primeng/stepper';

import {
  GrantConfig,
  GrantDialogResult,
  GrantSelection,
  ItemSelectionData,
  SelectedItem,
} from '../dialog.models';

interface ResolvedGrantItem {
  id: string;
  type: ShareableType;
  name: string;
  modifiedDate?: Date;
}
import { BdUiProgressIndicatorComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { ShareableBatchRun } from '../batch-run';
import { BdGrantFormComponent } from './grant-form/grant-form.component';
import { BdGrantPreviewStepComponent } from './grant-preview-step/grant-preview-step.component';

@Component({
  selector: 'bd-grant-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (progressState() === 'idle') {
      <p-stepper [value]="activeStep()" [linear]="true">
        <p-step-list>
          <p-step [value]="1">Ziel</p-step>
          <p-step [value]="2">Ressourcen</p-step>
        </p-step-list>
        <p-step-panels>
          <p-step-panel [value]="1">
            <ng-template #content let-activateCallback="activateCallback">
              <bd-grant-form
                [itemCount]="items.length"
                [initialPermission]="initialPermission"
                (configChange)="onGrantConfigChange($event)"
              ></bd-grant-form>
              <div class="flex flex-row gap-4 justify-end w-full pt-4">
                <button
                  pButton
                  [outlined]="true"
                  severity="secondary"
                  (click)="ref.close(null)"
                >
                  <span>Abbrechen</span>
                </button>
                <button
                  pButton
                  bdDialogPrimaryAction
                  [raised]="true"
                  [disabled]="!currentGrantConfig()"
                  (click)="onStartPreview(activateCallback)"
                >
                  <span>Weiter</span>
                </button>
              </div>
            </ng-template>
          </p-step-panel>

          <p-step-panel [value]="2">
            <ng-template #content let-activateCallback="activateCallback">
              @if (currentGrantConfig() && previewActive()) {
                <bd-grant-preview-step
                  [items]="items"
                  [grantConfig]="currentGrantConfig()!"
                  (previewReady)="onPreviewReady($event)"
                  (selectionChange)="onSelectionChange($event)"
                ></bd-grant-preview-step>
              }
              <div class="flex flex-row gap-4 justify-end w-full pt-4">
                <button
                  pButton
                  [outlined]="true"
                  severity="secondary"
                  (click)="onBackFromPreview(activateCallback)"
                >
                  <span>Zurück</span>
                </button>
                <button
                  pButton
                  bdDialogPrimaryAction
                  [raised]="true"
                  [disabled]="!previewFinished()"
                  (click)="onSubmitGrant()"
                >
                  <span>Freigeben</span>
                </button>
              </div>
            </ng-template>
          </p-step-panel>
        </p-step-panels>
      </p-stepper>
    } @else {
      <bd-ui-progress-indicator
        [state]="progressState()"
        actionLabel="Erteile Freigaben…"
        doneLabel="Alle Freigaben wurden erfolgreich erteilt."
        [completedCount]="completedCount()"
        [totalCount]="totalGrantCount()"
        [errorCount]="errorCount()"
        (closed)="onClose()"
      ></bd-ui-progress-indicator>
    }
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    ButtonDirective,
    Stepper,
    StepList,
    Step,
    StepPanels,
    StepPanel,
    BdGrantFormComponent,
    BdGrantPreviewStepComponent,
    BdUiProgressIndicatorComponent,
  ],
})
export class BdGrantDialogComponent {
  public readonly ref = inject(DynamicDialogRef);
  public readonly config =
    inject<DynamicDialogConfig<ItemSelectionData>>(DynamicDialogConfig);

  private readonly _grantsService = inject(BdGrantsService);

  public readonly items: SelectedItem[] = this.config.data?.items ?? [];
  public readonly initialPermission = this.config.data?.initialPermission;

  public readonly activeStep = signal<number>(1);
  private readonly _batch = new ShareableBatchRun(inject(DestroyRef));
  public readonly progressState = this._batch.state;
  public readonly currentGrantConfig = signal<GrantConfig | null>(null);
  public readonly previewActive = signal(false);
  public readonly previewFinished = signal(false);
  public readonly completedCount = this._batch.completed;
  public readonly errorCount = this._batch.failed;
  public readonly totalGrantCount = this._batch.total;

  // `null` means the preview step has not reported a selection yet, in which
  // case every originally selected item is granted by default.
  private _selectedRootIds: string[] | null = null;
  private _selectedDependencies: GrantSelection['dependencies'] = [];

  public constructor() {
    // Escape / × wait for the running grants; afterwards they report the same
    // result as "Schließen".
    bdDialogCloseBehavior({
      busy: () => this.progressState() === 'running',
      resultOnClose: () =>
        this.progressState() === 'done' ? this._result() : undefined,
    });
  }

  public onGrantConfigChange(config: GrantConfig | null): void {
    this.currentGrantConfig.set(config);
    this.previewFinished.set(false);
    this._selectedRootIds = null;
    this._selectedDependencies = [];
  }

  public onStartPreview(activateCallback: (step: number) => void): void {
    this.previewActive.set(true);
    this.previewFinished.set(false);
    activateCallback(2);
    this.activeStep.set(2);
  }

  public onBackFromPreview(activateCallback: (step: number) => void): void {
    this.previewActive.set(false);
    this.previewFinished.set(false);
    activateCallback(1);
    this.activeStep.set(1);
  }

  public onPreviewReady(success: boolean): void {
    this.previewFinished.set(success);
  }

  public onSelectionChange(selection: GrantSelection): void {
    this._selectedRootIds = selection.rootIds;
    this._selectedDependencies = selection.dependencies;
  }

  public onSubmitGrant(): void {
    const config = this.currentGrantConfig();
    if (!config) {
      return;
    }
    this._batch.run(this._buildAllGrantItems(), (item) =>
      this._grantsService.create(this._grant(item, config)),
    );
  }

  public onClose(): void {
    this.ref.close(this._result());
  }

  private _result(): GrantDialogResult {
    return { granted: this.errorCount() === 0 };
  }

  private _buildAllGrantItems(): ResolvedGrantItem[] {
    const rootIds = this._selectedRootIds;
    const selectedRoots =
      rootIds === null
        ? this.items
        : this.items.filter((item) => rootIds.includes(item.id));

    const items: ResolvedGrantItem[] = selectedRoots.map((item) => ({
      id: item.id,
      type: item.type,
      name: item.displayName,
      modifiedDate: item.modifiedDate ? new Date(item.modifiedDate) : undefined,
    }));

    const seen = new Set(items.map((item) => `${item.type}::${item.id}`));
    for (const dep of this._selectedDependencies) {
      const key = `${dep.type}::${dep.id}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      items.push({ id: dep.id, type: dep.type, name: dep.name });
    }
    return items;
  }


  private _grant(item: ResolvedGrantItem, grantConfig: GrantConfig): ShareableGrant {
    return {
      shareableReference: {
        id: item.id,
        type: item.type,
        name: item.name,
        modifiedDate: item.modifiedDate ?? new Date(),
        createdDate: new Date(),
        parentFolderId: '',
        allowedShareablePermissions: [],
      } as ShareableObj,
      permission: grantConfig.permission,
      grantee: { id: grantConfig.granteeId, type: grantConfig.granteeType },
      createdAt: new Date(),
    };
  }
}
